import { useMutation } from "@apollo/client";
import {
  AcceptSharingInvitationDocument,
  CreateOrganizationDocument,
  CurrentUserOrganizationsDocument,
  OrganizationType,
} from "../../__generated__/graphql";
import useCurrentUserOrganizations from "../../hooks/useCurrentUserOrganizations";
import toast from "react-hot-toast";
import { motion } from "motion/react";
import FullLoading from "../../components/FullLoading";
import Logo from "../../components/Logo";
import { organizationTypeToName } from "../../lib/utils";
import useNav from "../../hooks/useNavigation";
import Redirect from "../../components/Redirect";
import { useAuth } from "../../hooks/useAuth";

interface orgInfo {
  name: string;
  description: string;
  type: OrganizationType;
}

const organizationsInfo: Record<OrganizationType, orgInfo> = {
  [OrganizationType.Artist]: {
    name: organizationTypeToName[OrganizationType.Artist],
    description:
      "Un espace personnel pour gérer vos projets, vos contrats et vos collaborations professionnelles. Restez organisé et connecté avec tous les acteurs de votre carrière.",
    type: OrganizationType.Artist,
  },
  [OrganizationType.Producer]: {
    name: organizationTypeToName[OrganizationType.Producer],
    description:
      "Centralisez vos projets et vos talents. Envoyez des contrats, partagez des documents importants et suivez l'avancement de vos productions. Une solution complète pour gérer efficacement vos collaborations artistiques.",
    type: OrganizationType.Producer,
  },
  [OrganizationType.Agency]: {
    name: organizationTypeToName[OrganizationType.Agency],
    description:
      "Orchestrez le succès de vos talents en pilotant efficacement leur carrière.",
    type: OrganizationType.Agency,
  },
  [OrganizationType.Other]: {
    name: organizationTypeToName[OrganizationType.Other],
    description: "Vous êtes un autre type d'organisation.",
    type: OrganizationType.Other,
  },
};

export default function CreateOrganization() {
  const { isAuthenticated, isAuthLoading } = useAuth();
  const navigateWithLog = useNav();

  const [acceptSharingInvitation, { loading: acceptSharingInvitationLoading }] =
    useMutation(AcceptSharingInvitationDocument);
  const {
    loading: isOrganizationLoading,
    error,
    isPartOfOrganization,
  } = useCurrentUserOrganizations();
  const [createOrganization, { loading: createOrganizationLoading }] =
    useMutation(CreateOrganizationDocument);

  const loading =
    isOrganizationLoading ||
    isAuthLoading ||
    acceptSharingInvitationLoading ||
    createOrganizationLoading;

  if (loading) return <FullLoading />;

  if (error) return <div className="text-red-600">Error: {error.message}</div>;

  if (!isAuthenticated) return <Redirect to="/signin" />;

  // Only redirect if we're certain the user is part of an organization
  // Use explicit check to avoid redirecting when isPartOfOrganization is undefined
  if (isPartOfOrganization === true) {
    return <Redirect to="/" />;
  }

  const handleCreateOrganization = async (
    organizationType: OrganizationType
  ) => {
    const response = confirm(
      `Est-ce que vous confirmez votre choix: ${organizationsInfo[organizationType].name} ?`
    );

    if (!response) return;

    try {
      const { data } = await createOrganization({
        variables: {
          createOrganizationInput: {
            name: "",
            organizationType,
          },
        },
        refetchQueries: [CurrentUserOrganizationsDocument],
      });

      toast.success("Organisation créée avec succès");

      // Use new token with the new organization
      localStorage.setItem("token", data?.createOrganization?.token || "");

      if (data?.createOrganization?.organization.id) {
        localStorage.setItem(
          "organizationId",
          data?.createOrganization?.organization.id
        );
      }

      const invitationCode = localStorage.getItem("invitationCode");
      if (invitationCode) {
        localStorage.removeItem("invitationCode");

        const { data: acceptSharingInvitationData } =
          await acceptSharingInvitation({
            variables: {
              acceptSharingInvitationInput: {
                invitationCode: invitationCode || "",
                toOrganizationId:
                  data?.createOrganization?.organization.id || "",
              },
            },
          });

        if (acceptSharingInvitationData?.acceptSharingInvitation) {
          navigateWithLog("/");
        } else {
          toast.error(
            "Une erreur est survenue lors de l'acceptation de l'invitation"
          );
        }
      } else {
        navigateWithLog("/");
      }
    } catch (error) {
      console.error(error);
      toast.error("Impossible de créer l'organisation");
    }
  };

  const organizationTypeCard = (type: OrganizationType) => {
    return (
      <div
        className="p-3 md:p-4 rounded-md cursor-pointer bg-card border border-border hover:bg-card/40 transition-all duration-300 hover:border-accent"
        onClick={() => handleCreateOrganization(type)}
        data-testid={`create-organization-card-${type}`}
      >
        <h2 className="text-base md:text-lg font-bold">
          {organizationsInfo[type].name}
        </h2>
        <div className="text-tertiary text-sm md:text-base">
          {organizationsInfo[type].description}
        </div>
      </div>
    );
  };

  return (
    <div
      className="bg-background min-h-screen"
      data-testid="create-organization-page"
    >
      <div className="flex flex-col justify-center items-center p-4 md:p-8 gap-4">
        <Logo />
      </div>

      <div className="flex flex-col items-center gap-4 bg-background text-primary px-4 pb-8 md:px-8">
        <motion.div
          className="w-full max-w-md md:max-w-2xl bg-dark border border-border rounded-lg px-4 py-6 md:py-8 text-primary"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div>
            <h1 className="text-xl md:text-2xl font-bold">Qui êtes-vous ?</h1>
            <p className="text-tertiary text-sm md:text-base">
              Pour vous proposer une expérience sur mesure, nous avons besoin de
              connaître votre type d'activité.
            </p>
          </div>

          <div className="flex flex-col gap-3 md:gap-4 mt-6 md:mt-8">
            {organizationTypeCard(OrganizationType.Artist)}
            {organizationTypeCard(OrganizationType.Producer)}
            {organizationTypeCard(OrganizationType.Agency)}
            {organizationTypeCard(OrganizationType.Other)}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
